"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { 
  EcommerceProductsProvider, 
  useEcommerceProducts, 
  AddToCartButton, 
  ProductSortDropdown, 
  ProductsFilter 
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Settings2, CircleAlert } from "lucide-react";


function ProductsGridConsumer() {
  const { 
    products, 
    loading, 
    error, 
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="163651" className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} data-index={i} className="h-[400px] border-border/40 bg-card/10 animate-pulse rounded-sm">
            <div className="h-48 bg-muted/30 border-b border-border/40" />
            <CardHeader className="space-y-2">
              <div className="h-4 bg-muted/40 rounded w-3/4" />
              <div className="h-4 bg-muted/40 rounded w-1/4" />
            </CardHeader>
            <CardContent>
              <div className="h-16 bg-muted/40 rounded w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center border border-destructive/30 bg-destructive/5 rounded-sm">
        <CircleAlert className="w-8 h-8 text-destructive mb-4" />
        <p className="text-sm text-muted-foreground mb-4 font-default">System encountered an error while retrieving service data.</p>
        <Button variant="outline" onClick={refetch} className="font-mono text-xs tracking-tighter">RETRY_CONNECTION</Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center border border-border/30 border-dashed rounded-sm bg-card/5">
        <Settings2 className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-default font-bold text-foreground mb-2">No Services Found</h3>
        <p className="text-sm text-muted-foreground max-w-md font-default">
          Adjust your filter parameters to locate the required digital infrastructure or connectivity tools.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {products.map((product, i) => (
          <Card 
            key={product.id} 
            data-index={i} 
            className="flex flex-col h-full border-border/50 bg-card/30 backdrop-blur-md shadow-sm hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:border-primary/40 transition-all duration-300 overflow-hidden group rounded-sm"
          >
            <Link to={product.link} className="block relative aspect-video bg-muted/20 overflow-hidden border-b border-border/50">
              {product.featured_image_url ? (
                <img 
                  src={product.featured_image_url} 
                  alt={product.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out opacity-90 group-hover:opacity-100" 
                  loading="lazy" 
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-secondary/10 text-muted-foreground font-mono text-xs">
                  [ENCRYPTED_ASSET]
                </div>
              )}
              <div className="absolute top-3 right-3 bg-background/80 backdrop-blur-md px-2 py-1 rounded-sm text-[10px] font-mono border border-border/50 text-primary uppercase tracking-widest shadow-sm">
                UID:{product.id.toString().padStart(4, '0')}
              </div>
            </Link>
            
            <CardHeader className="pb-3">
              <Link to={product.link} className="hover:text-primary transition-colors">
                <CardTitle className="text-lg font-default font-bold leading-tight line-clamp-2 tracking-tight">{product.title}</CardTitle>
              </Link>
              {product.price !== undefined && (
                <div className="font-mono text-xl font-bold text-primary mt-2 flex items-center gap-1">
                  <span className="text-xs opacity-60">Ξ</span>{product.price.toFixed(2)}
                </div>
              )}
            </CardHeader>
            
            <CardContent className="flex-grow pb-6">
              {product.short_description && (
                <div 
                  className="text-sm text-muted-foreground line-clamp-3 mb-4 font-default prose prose-sm dark:prose-invert prose-p:leading-relaxed" 
                  dangerouslySetInnerHTML={{ __html: product.short_description }} 
                />
              )}
              
              <div className="space-y-2 mt-4 pt-4 border-t border-border/20">
                <div className="flex justify-between items-center text-[10px] font-mono tracking-wider">
                  <span className="text-muted-foreground">SERVICE_STATUS</span>
                  <span className="text-foreground flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(0,255,255,0.8)]"></span>
                    ACTIVE
                  </span>
                </div>
                {product.categories && product.categories.length > 0 && (
                  <div className="flex justify-between items-center text-[10px] font-mono tracking-wider">
                    <span className="text-muted-foreground">CATEGORY</span>
                    <span className="text-foreground truncate max-w-[120px] text-right">
                      {product.categories[0].name}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
            
            <CardFooter className="pt-0 mt-auto">
              <AddToCartButton 
                productId={product.id} 
                className="w-full" 
                buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase h-11 rounded-sm shadow-lg transition-all" 
                text="Activate Service" 
                showQuantity={false} 
              />
            </CardFooter>
          </Card>
        ))}
      </div>

      {}
      {(hasNext || hasPrev) && (
        <div className="flex items-center justify-center gap-4 mt-8 pt-8 border-t border-border/10">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={prevPage} 
            disabled={!hasPrev}
            className="font-mono text-[10px] uppercase tracking-widest border-border/40 hover:bg-secondary/50 rounded-sm"
          >
            <ChevronLeft className="w-3 h-3 mr-1" />
            Prev
          </Button>
          <div className="font-mono text-[10px] text-muted-foreground px-4 py-2 bg-card/20 border border-border/20 rounded-sm tracking-widest">
            NODE {currentPage.toString().padStart(2, '0')} / {totalPages.toString().padStart(2, '0')}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={nextPage} 
            disabled={!hasNext}
            className="font-mono text-[10px] uppercase tracking-widest border-border/40 hover:bg-secondary/50 rounded-sm"
          >
            Next
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ShopContent() {
  
  const current_wp_query_params = typeof window !== 'undefined' && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : {};
  
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="shop-content" className="dark bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="mb-10 md:mb-16 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-secondary/30 border border-border/40 text-[10px] font-mono text-muted-foreground mb-6 uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            Network Status: Online
          </div>
          <h1 className="text-4xl md:text-6xl font-default font-bold tracking-tighter mb-6">
            Digital <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-primary/40">Services</span>
          </h1>
          <p className="text-lg text-muted-foreground font-default leading-relaxed max-w-2xl">
            Secure access to premium SaaS tools, global connectivity, and privacy infrastructure. All optimized for the digital nomad and powered by decentralized crypto payments.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          {}
          <SidebarProvider className="min-h-0 h-auto flex-col md:flex-row border border-border/40 rounded-sm overflow-hidden bg-card/10 backdrop-blur-md shadow-2xl">
            
            {}
            <Sidebar collapsible="none" className="w-full md:w-72 border-b md:border-b-0 md:border-r border-border/40 bg-sidebar/20">
              <SidebarHeader className="p-5 border-b border-border/20 bg-card/20">
                <div className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-primary" />
                  <h2 className="font-mono text-xs font-bold tracking-[0.15em] uppercase text-foreground">Service_Filters</h2>
                </div>
              </SidebarHeader>
              <SidebarContent className="p-5 custom-scrollbar">
                <ProductsFilter />
              </SidebarContent>
            </Sidebar>

            {}
            <SidebarInset className="flex-1 bg-transparent p-5 md:p-10 min-w-0">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 pb-4 border-b border-border/10">
                <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.2em]">
                  Available_Services 
                </div>
                <div className="w-full sm:w-auto flex items-center gap-3">
                  <ProductSortDropdown label="Sort Output:" />
                </div>
              </div>
              
              <ProductsGridConsumer />
            </SidebarInset>
            
          </SidebarProvider>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}